import React, { useRef, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Box, SxProps, Theme, Typography, CircularProgress } from "@mui/material";
import {
  DescriptionVoiceRecorder,
} from "@/Components/VoiceRecorder";

interface RichTextEditorProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  readOnly?: boolean;
  // Voice recording props (optional)
  enableVoiceRecording?: boolean;
  onAudioAttachment?: (audioBlob: Blob, transcript: string) => void;
  onAIImprove?: (transcript: string) => Promise<string>;
  maxLength?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Write something...",
  style,
  readOnly = false,
  enableVoiceRecording = false,
  onAudioAttachment,
  onAIImprove,
  maxLength,
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ script: "sub" }, { script: "super" }],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [{ color: [] }, { background: [] }],
      ["link"],
      ["clean"],
      [{ align: [] }],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "script",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "color",
    "background",
    "link",
    "align",
  ];

  const containerSx: SxProps<Theme> = {
    height: "200px",
    position: "relative",
  };

  // Helper function to strip HTML tags
  const stripHtmlTags = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (
    <Box sx={containerSx}>
      <div className="editor-container" style={{ position: "relative" }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
          style={{
            ...style,
            height: "200px",
            opacity: readOnly ? 0.7 : 1,
          }}
        />

        {/* Processing Message Overlay */}
        {enableVoiceRecording && processingMessage && (
          <Box
            sx={{
              position: "absolute",
              top: "60%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              padding: "6px 12px",
              borderRadius: "16px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
              zIndex: 10,
              border: "1px solid #e2e8f0",
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={14} thickness={4} />
            <Typography
              sx={{
                fontSize: "12px",
                color: "#475569",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {processingMessage}
            </Typography>
          </Box>
        )}
      </div>

      {/* Voice Recorder positioned at bottom right */}
      {enableVoiceRecording && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            zIndex: 10,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(30, 30, 30, 0.1)"
                : "rgba(255, 255, 255, 0.1)",
            borderRadius: "6px",
            padding: "3px 8px",
            backdropFilter: "blur(4px)",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 6px rgba(0, 0, 0, 0.3)"
                : "0 2px 6px rgba(0, 0, 0, 0.08)",
            border: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.05)",
          }}
        >
          <DescriptionVoiceRecorder
            onTranscriptChange={(newText) => {
              // Use Quill API to insert text at the end of the editor
              if (quillRef.current) {
                const quill = quillRef.current.getEditor();
                const currentLength = quill.getLength();
                const plainCurrent = stripHtmlTags(value || "");

                if (newText.startsWith(plainCurrent)) {
                  // newText contains the full text, extract just the new part
                  const addedText = newText.substring(plainCurrent.length);
                  // Insert at the end (currentLength - 1 to account for the trailing newline Quill adds)
                  quill.insertText(currentLength - 1, addedText);
                } else {
                  // Replace entire content
                  quill.setText(newText);
                }
              } else {
                // Fallback if quillRef is not available
                onChange(newText);
              }
            }}
            onAudioAttachment={onAudioAttachment}
            onAIImprove={onAIImprove}
            onRecordingChange={setIsVoiceRecording}
            onAIProcessingChange={setIsAIProcessing}
            onFinalizingChange={setIsFinalizing}
            onProcessingMessageChange={setProcessingMessage}
            currentText={stripHtmlTags(value || "")}
            currentHtmlValue={value || ""}
            onHtmlRestore={(html) => {
              // Directly set HTML content to preserve formatting (used by undo/redo)
              onChange(html);
            }}
            maxLength={maxLength}
            language="en-US"
          />
        </Box>
      )}
      <style>{`
        .editor-container {
          height: 200px;
          display: flex;
          flex-direction: column;
        }
        .editor-container .quill {
          height: 200px;
          display: flex;
          flex-direction: column;
          border: 1px solid #ccc;
          border-radius: 4px;
          overflow: hidden;
        }
        .editor-container .ql-toolbar {
          flex-shrink: 0;
          position: sticky;
          top: 0;
          z-index: 100;
          background: var(--toolbar-bg, #ffffff);
          border: none !important;
          border-bottom: 1px solid #ccc !important;
          border-radius: 4px 4px 0 0;
        }
        .editor-container .ql-container {
          flex: 1;
          overflow-y: auto;
          background: transparent;
          min-height: 0;
          border: none !important;
          position: relative;
        }
        .editor-container .ql-editor {
          height: auto !important;
          min-height: 100px;
          padding: 12px 15px;
          padding-top: 12px !important;
          padding-bottom: 40px !important;
        }
        .editor-container .wave-overlay-container {
          top: 43px !important;
        }
        .editor-container .ai-loading-overlay-container {
          top: 43px !important;
          z-index: 8 !important;
        }
      `}</style>
    </Box>
  );
};

export default RichTextEditor;
